import moment from "moment";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { useNavigate } from "react-router";

import {
  DATE_FORMAT,
  DEFAULT_SLIDESHOW_FOLDER_NAME,
  FileModel,
  MediaTypesEnum,
  bytesToKBytes,
  bytesToMBytes,
} from "dash-common";
import {
  Box,
  Checkbox,
  FileTypeIcon,
  Flex,
  Icon,
  SortHeader,
  TableWitPagination,
  useScreenResolution,
  Text,
  SortContext,
} from "dash-ui";

import { Media } from "gql/generated.graphql";
import { BulkActionsContext } from "common/contexts/bulkActionsContext/bulkActionsContext";
import { RoutesPath } from "flow-components/routes/routes";
import ActionButtons from "containers/properties/PropertyMedia/components/MediaFilesView/components/MediaFilesList/components/MediaFileItem/components/ActionButtons";
import BrandingTypeBadge from "containers/properties/PropertyMedia/components/BrandingTypeBadge/BrandingTypeBadge";

const StyledTable = styled(TableWitPagination)`
  & table {
    & tbody {
      & tr {
        & td:last-child > div {
          display: flex;
          visibility: hidden;
          position: unset;
        }
        &:hover td:last-child > div {
          visibility: visible;
        }
      }
    }
  }
`;

const MediaFilesTable = ({
  media,
  setFileToPreviewId,
  propertyId = "",
}: {
  media: { files: any[]; folders: any[] };
  setFileToPreviewId: (id: string) => void;
  propertyId?: string;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: sort } = useContext(SortContext);
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const { checkIsSelected, selectMedia, setIsAllSelected, resetState } =
    useContext(BulkActionsContext);

  const tableData = useMemo(() => {
    if (sort?.fieldName === "sizeInBytes") {
      return [...media.folders, ...media.files].sort((a, b) => {
        const aSize = a.sizeInBytes;
        const bSize = b.sizeInBytes;
        if (aSize < bSize) return sort.sortOrder === 1 ? -1 : 1;
        if (aSize > bSize) return sort.sortOrder === 1 ? 1 : -1;
        return 0;
      });
    }
    return [...media.folders, ...media.files];
  }, [media, sort]);

  const columns = useMemo(() => {
    return [
      {
        Header: () => (
          <SortHeader accessor="name">
            {isMobile ? t("common:labels:file") : t("common:labels:fileName")}
          </SortHeader>
        ),
        Cell: ({
          value,
          row,
        }: {
          value: FileModel;
          row: { original: any; index: number };
        }) => {
          const {
            folderName,
            type,
            _id: mediaId,
            brandingType,
            systemFolderType,
          } = row.original;
          const isFolder = type === MediaTypesEnum.FOLDER;

          return (
            <Flex
              alignItems="center"
              onClick={() => {
                if (isFolder) {
                  resetState();
                  return navigate(
                    RoutesPath.PropertyMediaFolder.replace(
                      ":id",
                      propertyId
                    ).replace(":folderId", mediaId)
                  );
                }
                setFileToPreviewId(mediaId);
              }}
            >
              {!isFolder && (
                <Checkbox
                  mr={6}
                  onChange={(checked) => {
                    selectMedia([{ id: mediaId, value: checked }]);
                    if (!checked) {
                      setIsAllSelected(checked);
                    }
                  }}
                  checked={checkIsSelected(mediaId)}
                />
              )}
              {isFolder || !value ? (
                <Icon color={theme.colors.n60} name="folder" size={20} mr={2} />
              ) : (
                <FileTypeIcon mr={2} sm file={value} />
              )}
              <Box flex="1" overflow="hidden">
                {isMobile ? (
                  <Box>
                    <Text ellipsis>{folderName || value?.name}</Text>
                    <Box mt={1}>
                      <BrandingTypeBadge
                        brandingType={brandingType as any}
                        systemFolderType={systemFolderType as any}
                        isFolder={isFolder}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Flex alignItems="center">
                    <Box minWidth={0} overflow="hidden" mr={5}>
                      <Text ellipsis>{folderName || value?.name}</Text>
                    </Box>
                    <Box flex="0 0 auto">
                      <BrandingTypeBadge
                        brandingType={brandingType as any}
                        systemFolderType={systemFolderType as any}
                        isFolder={isFolder}
                      />
                    </Box>
                  </Flex>
                )}
              </Box>
            </Flex>
          );
        },
        accessor: "files.0",
        ...(isMobile ? { sticky: "left", width: 200 } : {}),
      },
      {
        Header: () => (
          <SortHeader accessor="sizeInBytes">
            {t("common:labels:size")}
          </SortHeader>
        ),
        accessor: "sizeInBytes",
        Cell: ({ value }: { value: number }) =>
          bytesToMBytes(value) > 1
            ? t("common:labels.mBytes", {
                size: bytesToMBytes(value),
              })
            : t("common:labels.kBytes", {
                size: bytesToKBytes(value),
              }),
        ...(isMobile ? { width: 82 } : {}),
      },
      {
        Header: () => (
          <SortHeader accessor="createdAt">
            {t("common:labels:added")}
          </SortHeader>
        ),
        accessor: "createdAt",
        Cell: ({ value }: { value: Date }) => moment(value).format(DATE_FORMAT),
        ...(isMobile ? { width: 105 } : {}),
      },
      {
        accessor: "_id",
        Cell: ({ row }: { row: { original: Media } }) => {
          return (
            <ActionButtons
              media={row.original}
              deletable={
                row.original.folderName !== DEFAULT_SLIDESHOW_FOLDER_NAME
              }
            />
          );
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, isMobile, theme, checkIsSelected]);

  return (
    <StyledTable
      w="100%"
      tableProps={{
        data: tableData,
        columns,
      }}
      paginationProps={{
        previousButtonLabel: t("common:labels.previous"),
        nextButtonLabel: t("common:labels.next"),
        label: t("pages:propertyPage.paginationMediaLabel"),
      }}
    />
  );
};

export default MediaFilesTable;
